import { useTranslation } from "react-i18next";
import { Mic, MicOff } from "lucide-react";
import { Button, cn } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";

export interface CallControlsProps {
  /** Whether the call is muted */
  isMuted: boolean;
  /** Whether the call is connected and controls should be enabled */
  isConnected: boolean;
  /** Callback when mute toggle is clicked */
  onToggleMute: () => void;
  /** Callback when hang up is clicked */
  onHangUp: () => void;
  /** Additional className for the container */
  className?: string;
}

/**
 * CallControls - Control buttons for an active call.
 *
 * Provides mute/unmute toggle and a full-width hang up button.
 *
 * @example
 * ```tsx
 * <CallControls
 *   isMuted={isMuted}
 *   isConnected={callStatus === 'open'}
 *   onToggleMute={handleToggleMute}
 *   onHangUp={handleHangUp}
 * />
 * ```
 */
export function CallControls({ isMuted, isConnected, onToggleMute, onHangUp, className }: CallControlsProps) {
  const { t } = useTranslation("calling");

  return (
    <div className={cn("flex flex-col gap-3 p-4", className)}>
      {/* Mute Button */}
      <div className="flex justify-center">
        <Tooltip>
          <Tooltip.Trigger asChild>
            <Button
              variant={isMuted ? "secondary" : "outline"}
              size="sm"
              onClick={onToggleMute}
              disabled={!isConnected}
              className="gap-2"
            >
              {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              {isMuted ? t("Unmute") : t("Mute")}
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>{isMuted ? t("Unmute microphone") : t("Mute microphone")}</Tooltip.Content>
        </Tooltip>
      </div>

      {/* End Call Button - Full Width */}
      <Button variant="destructive" onClick={onHangUp} className="w-full">
        {t("End call")}
      </Button>
    </div>
  );
}
